import { useState, useEffect, useRef } from 'react'
import { Card, Button } from '@trafiflow/shared-ui'
import { RefreshCw, Trash2, ChevronDown, ChevronUp } from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import apiClient from '../../../../shared/lib/api'

interface QueueAttributes {
  queueUrl: string
  main: {
    visible: string
    notVisible: string
    delayed: string
  }
  dlq: {
    url: string
    messages: string
    error?: string
  }
}

interface LogEntry {
  id: number
  timestamp: Date
  visible: string
  notVisible: string
  delayed: string
  dlqMessages: string
  error?: string
}

async function getQueueAttributes() {
  const response = await apiClient.get('/debug/queue')
  return response.data
}

export function QueueDebug() {
  const [logs, setLogs] = useState<LogEntry[]>([])
  const [showLogs, setShowLogs] = useState(true)
  const nextId = useRef(1)
  const logsEndRef = useRef<HTMLDivElement>(null)

  const { data, isLoading, error, refetch } = useQuery<QueueAttributes>({
    queryKey: ['debug', 'queue'],
    queryFn: getQueueAttributes,
    refetchInterval: 5000, // Auto-refresh every 5s
  })

  // Capture a log entry every time fresh data arrives
  useEffect(() => {
    if (!data) return
    setLogs(prev => {
      const entry: LogEntry = {
        id: nextId.current++,
        timestamp: new Date(),
        visible: data.main.visible,
        notVisible: data.main.notVisible,
        delayed: data.main.delayed,
        dlqMessages: data.dlq.messages,
        error: data.dlq.error || undefined,
      }
      // Keep last 200 entries
      const updated = [...prev, entry]
      if (updated.length > 200) updated.splice(0, updated.length - 200)
      return updated
    })
  }, [data])

  // Auto-scroll to the latest log
  useEffect(() => {
    if (showLogs) {
      logsEndRef.current?.scrollIntoView({ behavior: 'smooth' })
    }
  }, [logs, showLogs])

  const clearLogs = () => {
    setLogs([])
    nextId.current = 1
  }

  const formatTime = (d: Date) =>
    d.toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' })

  // Determine if a value changed compared to the previous entry
  const changed = (idx: number, key: keyof Pick<LogEntry, 'visible' | 'notVisible' | 'delayed' | 'dlqMessages'>) => {
    if (idx === 0) return false
    return logs[idx][key] !== logs[idx - 1][key]
  }

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-tf-text">Queue Debugger</h1>
        <Button onClick={() => refetch()} disabled={isLoading} variant="secondary" size="sm">
          <RefreshCw className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {error && (
        <Card className="p-4 bg-tf-feedback-error-bg border-red-200">
          <p className="text-red-700">Error fetching queue stats: {(error as Error).message}</p>
        </Card>
      )}

      {data && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Main Queue Stats */}
          <Card className="p-6">
            <h3 className="text-lg font-semibold mb-4 text-tf-text">Main Queue</h3>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-tf-bg-muted p-4 rounded-lg">
                  <span className="text-sm text-tf-text-muted block">Visible Messages</span>
                  <span className="text-2xl font-bold text-blue-600">{data.main.visible}</span>
                </div>
                <div className="bg-tf-bg-muted p-4 rounded-lg">
                  <span className="text-sm text-tf-text-muted block">In Flight (Processing)</span>
                  <span className="text-2xl font-bold text-amber-600">{data.main.notVisible}</span>
                </div>
                <div className="bg-tf-bg-muted p-4 rounded-lg">
                  <span className="text-sm text-tf-text-muted block">Delayed</span>
                  <span className="text-2xl font-bold text-gray-600">{data.main.delayed}</span>
                </div>
              </div>
              <div className="text-xs text-tf-text-muted break-all">
                URL: {data.queueUrl}
              </div>
            </div>
          </Card>

          {/* DLQ Stats */}
          <Card className="p-6">
            <h3 className="text-lg font-semibold mb-4 text-tf-text">Dead Letter Queue (DLQ)</h3>
            <div className="space-y-4">
              <div className="grid grid-cols-1 gap-4">
                <div className="bg-tf-bg-muted p-4 rounded-lg">
                  <span className="text-sm text-tf-text-muted block">Messages (Failed)</span>
                  <span className="text-2xl font-bold text-red-600">{data.dlq.messages}</span>
                </div>
              </div>
              {data.dlq.error && (
                <div className="text-sm text-red-600 bg-red-50 p-2 rounded">
                  Error checking DLQ: {data.dlq.error}
                </div>
              )}
              <div className="text-xs text-tf-text-muted break-all">
                URL: {data.dlq.url}
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Poll History Log */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={() => setShowLogs(prev => !prev)}
            className="flex items-center gap-2 text-lg font-semibold text-tf-text hover:opacity-80 transition-opacity"
          >
            {showLogs ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
            Poll History
            <span className="text-sm font-normal text-tf-text-muted">({logs.length} entries)</span>
          </button>
          {logs.length > 0 && (
            <Button onClick={clearLogs} variant="secondary" size="sm">
              <Trash2 className="w-4 h-4 mr-2" />
              Clear
            </Button>
          )}
        </div>

        {showLogs && (
          <div className="overflow-auto max-h-80 rounded-lg border border-tf-border">
            {logs.length === 0 ? (
              <p className="text-sm text-tf-text-muted p-4 text-center">
                Waiting for data… logs will appear as the queue is polled every 5s.
              </p>
            ) : (
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-tf-bg-muted z-10">
                  <tr className="text-left text-xs text-tf-text-muted">
                    <th className="px-3 py-2">#</th>
                    <th className="px-3 py-2">Time</th>
                    <th className="px-3 py-2">Visible</th>
                    <th className="px-3 py-2">In Flight</th>
                    <th className="px-3 py-2">Delayed</th>
                    <th className="px-3 py-2">DLQ</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-tf-border">
                  {logs.map((log, idx) => (
                    <tr key={log.id} className="hover:bg-tf-bg-muted/50 transition-colors">
                      <td className="px-3 py-1.5 text-tf-text-muted tabular-nums">{log.id}</td>
                      <td className="px-3 py-1.5 text-tf-text-muted tabular-nums font-mono text-xs">
                        {formatTime(log.timestamp)}
                      </td>
                      <td className={`px-3 py-1.5 tabular-nums font-medium ${changed(idx, 'visible') ? 'text-blue-600' : 'text-tf-text'}`}>
                        {log.visible}
                      </td>
                      <td className={`px-3 py-1.5 tabular-nums font-medium ${changed(idx, 'notVisible') ? 'text-amber-600' : 'text-tf-text'}`}>
                        {log.notVisible}
                      </td>
                      <td className={`px-3 py-1.5 tabular-nums font-medium ${changed(idx, 'delayed') ? 'text-gray-600' : 'text-tf-text'}`}>
                        {log.delayed}
                      </td>
                      <td className={`px-3 py-1.5 tabular-nums font-medium ${changed(idx, 'dlqMessages') ? 'text-red-600 font-bold' : 'text-tf-text'}`}>
                        {log.dlqMessages}
                        {log.error && <span className="text-red-400 ml-1" title={log.error}>⚠</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            <div ref={logsEndRef} />
          </div>
        )}
      </Card>

      <Card className="p-6">
        <h3 className="text-lg font-semibold mb-2 text-tf-text">How to Debug</h3>
        <ul className="list-disc list-inside space-y-2 text-sm text-tf-text-muted">
          <li><strong>Visible:</strong> Messages waiting to be picked up. If this is high, the worker might be down or slow.</li>
          <li><strong>In Flight:</strong> Messages currently being processed by a worker. If stuck here for more than 15 mins, they might be timing out.</li>
          <li><strong>DLQ Messages:</strong> Messages that failed processing 3 times. These are permanently failed jobs.</li>
        </ul>
      </Card>
    </div>
  )
}
